import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

const CHAPTERS = [
  {
    n: "01",
    title: "Veteran & family-owned",
    text: "Built on Army values: show up on time, do it right, stand behind the work. Every order supports a local Chandler family — not a franchise.",
  },
  {
    n: "02",
    title: "A deep clean that cares",
    text: "Hypoallergenic, fabric-safe processes that evict dust mites, respect delicate fibers, and bring bedding back fluffy and fresh.",
  },
  {
    n: "03",
    title: "Guaranteed, personally",
    text: "If it's not right, we redo it free. You'll always talk to Wais, the owner — never a call center.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="why-us" ref={ref} data-testid="manifesto" className="grain relative overflow-hidden bg-brand-navy py-24 text-white lg:py-32">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_0%,rgba(37,99,235,0.18),transparent_55%),radial-gradient(circle_at_90%_80%,rgba(245,158,11,0.1),transparent_50%)]" />

      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-2">
        <div className="lg:sticky lg:top-40 lg:self-start">
          <Reveal>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-amber-300 sm:text-sm">
              <Star className="h-4 w-4 fill-fall-amber text-fall-amber" /> The Oxford way
            </p>
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Why neighbors
              <span className="text-outline block font-extrabold">choose Oxford.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <motion.div style={{ y: imgY }} className="mt-10 overflow-hidden rounded-[2rem] border border-white/15 shadow-2xl shadow-black/40">
              <img
                data-testid="manifesto-van-image"
                src="/images/branded_van.webp"
                onError={(e) => (e.currentTarget.src = "/images/branded_van.png")}
                alt="Oxford Cleaners branded navy and amber delivery van"
                className="aspect-[4/3] w-full object-cover"
              />
            </motion.div>
            <p className="mt-4 text-sm text-slate-400">Free pickup &amp; delivery — Monday through Saturday.</p>
          </Reveal>
        </div>

        <div className="flex flex-col gap-6">
          {CHAPTERS.map((ch, i) => (
            <Reveal key={ch.n} delay={i * 0.1}>
              <div className="group rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur transition-colors hover:border-fall-amber/40">
                <span className="text-outline font-display text-5xl font-extrabold">{ch.n}</span>
                <h3 className="mt-4 font-display text-xl font-bold sm:text-2xl">{ch.title}</h3>
                <p className="mt-3 text-base leading-relaxed text-slate-300">{ch.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
